import { isKeyHotkey } from 'is-hotkey';
import isUrl from 'is-url';
import { Editor, Range, Transforms, Element, Node } from 'slate';

import {
  fixTripleClickSelection,
  LINK_DEFINITIONS,
} from '@xing-com/content-editor-plugin-helpers';

const withLinks = (editor) => {
  const { insertData, insertText, isInline, normalizeNode } = editor;

  // remove empty link nodes after insering a new line
  editor.normalizeNode = (entry) => {
    const [node] = entry;

    if (Element.isElement(node) && node.type === LINK_DEFINITIONS.type) {
      const linkText = Node.string(node);

      if (linkText.length === 0) {
        unwrapLink(editor);
      }
    }
    normalizeNode(entry);
  };

  editor.isInline = (element) =>
    element.type === LINK_DEFINITIONS.type ? true : isInline(element);

  editor.insertText = (text) => {
    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertText(text);
    }
  };

  editor.insertData = (data) => {
    const text = data.getData('text/plain');

    if (text && isUrl(text)) {
      wrapLink(editor, text);
    } else {
      insertData(data);
    }
  };

  return editor;
};

const currentLinkHref = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: (n) => n.type === LINK_DEFINITIONS.type,
  });
  return link?.[0].data?.href || '';
};

const isLinkActive = (editor) => {
  const [link] = Editor.nodes(editor, {
    match: (n) => n.type === LINK_DEFINITIONS.type,
  });
  return !!link;
};

const unwrapLink = (editor) => {
  Transforms.unwrapNodes(editor, {
    match: (n) => n.type === LINK_DEFINITIONS.type,
  });
};

const wrapLink = (editor, url) => {
  const hasLink = isLinkActive(editor);
  if (hasLink) unwrapLink(editor);

  const { selection } = editor;
  const isCollapsed = !hasLink && selection && Range.isCollapsed(selection);
  const link = {
    type: LINK_DEFINITIONS.type,
    data: { href: url },
    children: isCollapsed ? [{ text: url }] : [],
  };

  fixTripleClickSelection(editor);

  if (isCollapsed) {
    Transforms.insertNodes(editor, link);
  } else {
    Transforms.wrapNodes(editor, link, { split: true });
    Transforms.collapse(editor, { edge: 'end' });
  }
};

const isValidUrl = (url) => {
  if (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('ftp://')
  ) {
    return true;
  }
  if (url.startsWith('mailto:')) {
    const email = url.replace(/^mailto:/, '');
    return isValidEmail(email);
  }
  return false;
};

const isValidEmail = (email) =>
  email.includes('@') &&
  !email.startsWith('@') &&
  !email.endsWith('@') &&
  !email.includes('/');

const fixUrl = (url) => {
  // if link starts with `http://`, `https://`, `mailto:` or `ftp://`, keep that untouched
  if (isValidUrl(url)) {
    return url;
  }
  // if link has an `@` symbol, does not have an `/` and doesn't start with the above prefixes,
  // prepend `mailto:` to it (so it opens with email client)
  if (isValidEmail(url)) {
    return 'mailto:' + url;
  }
  // if it starts with `mailto://`, it is wrong, so return null
  if (url.startsWith('mailto://')) {
    return null;
  }
  // otherwise, prepend `https://` to it
  return 'https://' + url;
};

const insertLink = (editor, url, { typeLink = LINK_DEFINITIONS.type } = {}) => {
  if (editor.selection) {
    let _url = fixUrl(url.trim());
    wrapLink(editor, _url, { typeLink });
  }
};

const removeLinkMark = (event, editor) => {
  if (isKeyHotkey('right', event)) {
    event.preventDefault();
    Transforms.move(editor, { unit: 'offset' });
  }
};

export {
  fixUrl,
  withLinks,
  insertLink,
  isLinkActive,
  unwrapLink,
  currentLinkHref,
  removeLinkMark,
};
